import React, { useState, useMemo } from 'react';
import { markActioned } from './indexedDBStore';
import { filterByPeriod } from './kpiCalculator';
import { AD_PREFIX_RE } from '../creatorType';

function shortNameForSlack(fullName) {
  if (!fullName) return 'Unnamed ad';
  let s = fullName.replace(AD_PREFIX_RE, '');
  const cutAt = s.search(/_HOOK|_DUR|_ugc/i);
  if (cutAt > 0) s = s.slice(0, cutAt);
  return s.slice(0, 50);
}

function generateSlackPayload(ads, testMode) {
  const date = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const prefix = testMode ? '[TEST] ' : '';
  let text = prefix + '*' + ads.length + (ads.length === 1 ? ' winner' : ' winners') + ' to promote* — ' + date + '\n\n';
  ads.forEach((ad) => {
    const name = shortNameForSlack(ad.ad_name_sample);
    const cpa = ad.cpa ? Math.round(ad.cpa) : '?';
    const purchases = ad.achats || 0;
    text += '• `' + ad.core_id + '` — ' + name + '  ·  ' + purchases + ' purchases · €' + cpa + ' CPA\n';
  });
  return { text };
}

function generatePlainMsg(ads) {
  const date = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  let msg = ads.length + (ads.length === 1 ? ' winner' : ' winners') + ' to promote — ' + date + '\n\n';
  ads.forEach((ad) => {
    const name = shortNameForSlack(ad.ad_name_sample);
    const cpa = ad.cpa ? Math.round(ad.cpa) : '?';
    const purchases = ad.achats || 0;
    msg += '• ' + ad.core_id + ' — ' + name + '  ·  ' + purchases + ' purchases · €' + cpa + ' CPA\n';
  });
  return msg;
}

function AdRow({ ad, selected, actioned, onToggle, onMarkActioned }) {
  const shortName = (ad.ad_name_sample || '').slice(0, 60);
  return (
    <div className={'sendua-row' + (selected ? ' selected' : '') + (actioned ? ' actioned' : '')}>
      <span className="sendua-checkbox" onClick={onToggle}>
        {selected ? '✓' : ''}
      </span>
      <div className="sendua-row-main">
        <div className="sendua-row-name">
          {shortName}<span className="sendua-row-id"> · #{ad.core_id}</span>
        </div>
        <div className="sendua-row-meta">
          <span className={'sendua-verdict ' + (ad.verdict === 'winner' ? 'winner' : 'borderline')}>
            ● {ad.verdict === 'winner' ? 'Winner' : 'Borderline'}
          </span>
        </div>
      </div>
      <div className="sendua-row-stats">
        <span><strong>{ad.achats ?? 0}</strong><small>purchases</small></span>
        <span><strong>€{ad.cpa ? ad.cpa.toFixed(1) : '—'}</strong><small>CPA</small></span>
        <span><strong>€{Math.round(ad.spend || 0)}</strong><small>spend</small></span>
      </div>
      <button className="sendua-mark-btn" onClick={onMarkActioned} title="Mark actioned">✓</button>
    </div>
  );
}

export default function SendToUAModal({
  isOpen, onClose,
  winnersToPromote, kpis,
  actionedSet, onMarkActioned,
}) {
  const [period, setPeriod] = useState('30d');
  const [selected, setSelected] = useState(new Set());
  const [showAll, setShowAll] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [toast, setToast] = useState(null);

  const safe = Array.isArray(winnersToPromote) ? winnersToPromote : [];
  const filtered = useMemo(() => filterByPeriod(safe, period), [safe, period]);
  const visible = showAll ? filtered : filtered.slice(0, 10);

  const kpiTotal = kpis?.totalWinnersToPromote ?? 0;
  const kpiManque = kpis?.manqueAGagnerEUR ?? 0;
  const kpiRate = kpis?.promotionRate ?? 0;

  const toggleSel = (id) => setSelected(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => {
    const all = visible.every(a => selected.has(a.core_id));
    setSelected(p => { const n = new Set(p); if (all) visible.forEach(a => n.delete(a.core_id)); else visible.forEach(a => n.add(a.core_id)); return n; });
  };

  const doMarkActioned = async (id) => {
    try { await markActioned(id); } catch (e) { console.warn('markActioned err:', e); }
    if (onMarkActioned) onMarkActioned(id);
    setSelected(p => { const n = new Set(p); n.delete(id); return n; });
  };

  const doSend = async () => {
    const ads = filtered.filter(a => selected.has(a.core_id));
    if (ads.length === 0) return;
    const webhookUrl = process.env.REACT_APP_SLACK_WEBHOOK_URL;

    if (!webhookUrl) {
      const msg = generatePlainMsg(ads);
      try { await navigator.clipboard.writeText(msg); } catch (e) {}
      setToast({ type: 'error', text: '⚠ Slack not configured · Message copied to clipboard' });
      setTimeout(() => setToast(null), 4500);
      return;
    }

    setToast({ type: 'loading', text: testMode ? 'Sending test message…' : 'Sending to Slack…' });
    try {
      const payload = generateSlackPayload(ads, testMode);
      const response = await fetch(webhookUrl, { method: 'POST', body: JSON.stringify(payload) });
      if (!response.ok) throw new Error('Slack returned ' + response.status);
      if (!testMode) {
        for (const a of ads) {
          try { await markActioned(a.core_id); } catch (e) { console.warn('markActioned err:', e); }
          if (onMarkActioned) onMarkActioned(a.core_id);
        }
      }
      setSelected(new Set());
      setToast({
        type: 'success',
        text: testMode
          ? '✓ Test sent · ' + ads.length + ' ad(s) not marked'
          : '✓ Sent to #marketing_influencers · ' + ads.length + ' winner(s)',
      });
      setTimeout(() => setToast(null), 3500);
    } catch (err) {
      console.error('Slack send failed:', err);
      setToast({ type: 'error', text: '✗ Failed to send to Slack · Try again' });
      setTimeout(() => setToast(null), 4500);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="sendua-overlay" onClick={(e) => { if (e.target.classList.contains('sendua-overlay')) onClose(); }}>
      <div className="sendua-modal">
        <div className="sendua-header">
          <div className="sendua-title">
            <span className="sendua-title-icon">↗</span>
            <span>Send winners to UA</span>
          </div>
          <div className="period-group">
            {['1d', '3d', '7d', '14d', '30d'].map(p => (
              <button key={p}
                className={'period-btn' + (period === p ? ' active' : '')}
                onClick={() => setPeriod(p)}>{p}</button>
            ))}
          </div>
          <button className="sendua-close" onClick={onClose}>×</button>
        </div>

        <div className="sendua-body">
          <div className="sendua-kpis">
            <div className="sendua-kpi">
              <div className="sendua-kpi-label">Push to Main adset</div>
              <div className="sendua-kpi-value">{kpiTotal}</div>
              <div className="sendua-kpi-sub">≥5 purchases in a single run · CPA judged in BAU</div>
            </div>
            <div className="sendua-kpi">
              <div className="sendua-kpi-label">Lost potential</div>
              <div className="sendua-kpi-value">~€{kpiManque >= 1000 ? Math.round(kpiManque/1000) + 'k' : Math.round(kpiManque)}</div>
              <div className="sendua-kpi-sub">x50 avg scaling × €8 LTV</div>
            </div>
            <div className="sendua-kpi">
              <div className="sendua-kpi-label">Promotion rate</div>
              <div className="sendua-kpi-value">{kpiRate}%</div>
              <div className="sendua-kpi-sub">target: 60%+ winners promoted</div>
            </div>
          </div>

          <div className="sendua-bau-note">
            <span className="sendua-bau-icon">↗</span>
            Push each winner into all <b>3 BAU adsets</b>: iOS · Android · web-to-app
            <span className="sendua-bau-hint"> (web-to-app needs a user destination in AdManage)</span>
          </div>

          <div className="sendua-toolbar">
            <span className="sendua-select-all" onClick={toggleAll}>
              <span className="sendua-checkbox-mini">
                {visible.length > 0 && visible.every(a => selected.has(a.core_id)) ? '✓' : ''}
              </span>
              Select all
            </span>
            <div className="sendua-actions">
              <span
                className={'sendua-testmode' + (testMode ? ' on' : '')}
                onClick={() => setTestMode(!testMode)}
                data-tooltip="When ON: prefixes Slack message with [TEST] and does NOT mark ads as sent">
                <span className="sendua-toggle">
                  <span className="sendua-toggle-knob" />
                </span>
                🧪 Test mode
              </span>
              <button
                className="sendua-send-btn"
                onClick={() => selected.size > 0 && doSend()}
                disabled={selected.size === 0}>
                ✉ Send to UA ({selected.size})
              </button>
            </div>
          </div>

          <div className="sendua-list">
            {visible.length === 0 ? (
              <div className="sendua-empty">No winners to promote in the last {period}</div>
            ) : (
              visible.map((ad, i) => (
                <AdRow key={ad.core_id || i}
                  ad={ad}
                  selected={selected.has(ad.core_id)}
                  actioned={actionedSet && actionedSet.has(ad.core_id)}
                  onToggle={() => toggleSel(ad.core_id)}
                  onMarkActioned={() => doMarkActioned(ad.core_id)} />
              ))
            )}
            {filtered.length > 10 && (
              <div className="sendua-show-more" onClick={() => setShowAll(v => !v)}>
                {showAll ? '▴ Show top 10 only' : `▾ Show all ${filtered.length}`}
              </div>
            )}
          </div>
        </div>

        {toast && (
          <div className={'sendua-toast ' + toast.type}>
            {toast.type === 'loading' && <span className="sendua-spinner" />}
            <span>{toast.text}</span>
          </div>
        )}
      </div>
    </div>
  );
}
